import { CYCLE_ALL_REVIEWS_CSV_EXPORT_PATH, CYCLE_ALL_REVIEWS_PDF_EXPORT_PATH, CYCLE_REPORT_CSV_EXPORT_PATHS } from "#shared/cycleExportsContract.ts";
import { CycleRosterSortColumns, type CycleRosterSortColumn } from "#shared/reviewCycleSchemas.ts";
import type { CycleQuestionKind } from "#shared/cycleQuestionSchemas.ts";
import type { MemberFilters } from "#shared/reviewee-filters/memberFilters.ts";
import type { RosterFilters } from "#shared/reviewee-filters/rosterFilters.ts";
import type { ScoreBucketGranularity } from "#shared/scoreBuckets.ts";

const CycleDetailTabs = ["progression", "scores", "questions", "calibration", "themes"] as const;

export type CycleDetailTab = (typeof CycleDetailTabs)[number];

export type CycleExportFormat = "pdf" | "csv";

type CycleExportId = "all-reviews-pdf" | "all-reviews-csv" | "report-pdf" | "report-csv";

interface CycleExportDownload {
    path: string;
    input: object;
    fallbackFileName: string;
}

export interface CycleExportItem {
    id: CycleExportId;
    format: CycleExportFormat;
    download?: CycleExportDownload;
}

export interface CycleExportGroup {
    id: "all-reviews" | "report";
    items: CycleExportItem[];
}

export function resolveActiveTab(pathname: string): CycleDetailTab {
    const lastSegment = pathname.split("/").findLast(Boolean) ?? "";

    return CycleDetailTabs.find(tab => tab === lastSegment) ?? "progression";
}

function isRosterSortColumn(value: string | null): value is CycleRosterSortColumn {
    return CycleRosterSortColumns.some(column => column === value);
}

function toRosterOrder(sort: string | null, dir: "asc" | "desc" | null) {
    return isRosterSortColumn(sort) ? { sortBy: sort, sortDir: dir ?? "asc" } : { sortBy: "name" as const, sortDir: "asc" as const };
}

export interface CycleExportSelection {
    tab: CycleDetailTab;
    orgId: string;
    cycleId: string;
    sort: string | null;
    dir: "asc" | "desc" | null;
    granularity: ScoreBucketGranularity;
    band: number | null;
    searchTerm: string;
    cellId: string | null;
    questionType: CycleQuestionKind | null;
    memberFilters: MemberFilters;
    rosterFilters: RosterFilters;
}

function toReportInput({ tab, orgId, cycleId, sort, dir, granularity, band, searchTerm, cellId, questionType, memberFilters, rosterFilters }: CycleExportSelection) {
    const cycle = { orgId, cycleId, ...memberFilters };

    if (tab === "themes") {
        return cycle;
    }

    if (tab === "scores") {
        return { ...cycle, granularity, sortDir: dir ?? "desc", ...(band !== null && { band }), ...(searchTerm && { searchTerm }) };
    }

    if (tab === "calibration") {
        return { ...cycle, ...(cellId && { cellId }), ...(searchTerm && { searchTerm }) };
    }

    if (tab === "questions") {
        return { ...cycle, ...(questionType && { questionType }) };
    }

    return { ...cycle, ...rosterFilters, ...toRosterOrder(sort, dir) };
}

export function resolveCycleExports(selection: CycleExportSelection): CycleExportGroup[] {
    const { orgId, cycleId, tab } = selection;

    const allReviewsInput = { orgId, cycleId, ...selection.memberFilters, ...selection.rosterFilters, ...toRosterOrder(selection.sort, selection.dir) };

    return [
        {
            id: "all-reviews",
            items: [
                { id: "all-reviews-pdf", format: "pdf", download: { path: CYCLE_ALL_REVIEWS_PDF_EXPORT_PATH, input: allReviewsInput, fallbackFileName: "all-reviews.zip" } },
                { id: "all-reviews-csv", format: "csv", download: { path: CYCLE_ALL_REVIEWS_CSV_EXPORT_PATH, input: allReviewsInput, fallbackFileName: "all-reviews.csv" } }
            ]
        },
        {
            id: "report",
            items: [
                { id: "report-pdf", format: "pdf" },
                { id: "report-csv", format: "csv", download: { path: CYCLE_REPORT_CSV_EXPORT_PATHS[tab], input: toReportInput(selection), fallbackFileName: `${tab}-report.csv` } }
            ]
        }
    ];
}
